import { CancelRounded, CheckCircleRounded } from "@mui/icons-material";
import { Button, type ButtonProps } from "@mui/material";
import { useState } from "react";
import { useHumanInTheLoop } from "./HumanInTheLoopContext";
import { type HumanInTheLoopRequest } from "./human-in-the-loop";

type BasicHumanInTheLoopDecisionType = "approve" | "reject";

export interface HumanInTheLoopDecisionControlsProps {
  request: HumanInTheLoopRequest;
  isLoading?: boolean;
  actionIndex: number;
  approveLabel?: string;
  rejectLabel?: string;
  approveColor?: ButtonProps["color"];
  approveDisabled?: boolean;
  beforeApprove?: () => Promise<boolean>;
}

export const HumanInTheLoopDecisionControls = ({
  request,
  isLoading = false,
  actionIndex,
  approveLabel: approveLabelOverride,
  rejectLabel: rejectLabelOverride,
  approveColor = "success",
  approveDisabled = false,
  beforeApprove,
}: HumanInTheLoopDecisionControlsProps) => {
  const humanInTheLoop = useHumanInTheLoop();
  const [submittingDecision, setSubmittingDecision] =
    useState<BasicHumanInTheLoopDecisionType | null>(null);

  const submit = async (type: BasicHumanInTheLoopDecisionType) => {
    setSubmittingDecision(type);
    try {
      if (type === "approve" && beforeApprove && !(await beforeApprove()))
        return;
      humanInTheLoop?.setDecision(actionIndex, { type });
    } catch {
      return;
    } finally {
      setSubmittingDecision(null);
    }
  };

  const decided = !!humanInTheLoop?.decisions[actionIndex];
  const reviewConfig = request.review_configs[actionIndex];
  const canApprove = reviewConfig?.allowed_decisions.includes("approve");
  const canReject = reviewConfig?.allowed_decisions.includes("reject");

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div
        aria-label="Review decision actions"
        role="group"
        className="flex shrink-0 items-center gap-2 whitespace-nowrap"
      >
        {canReject && (
          <Button
            variant="contained"
            color="error"
            sx={{ backgroundColor: "#d32f2f" }}
            size="small"
            disabled={decided || isLoading || submittingDecision !== null}
            startIcon={<CancelRounded />}
            onClick={() => void submit("reject")}
          >
            {rejectLabelOverride ?? "Reject"}
          </Button>
        )}
        {canApprove && (
          <Button
            variant="contained"
            color={approveColor}
            sx={
              approveColor === "success"
                ? { backgroundColor: "#15803d" }
                : undefined
            }
            size="small"
            disabled={
              decided ||
              isLoading ||
              submittingDecision !== null ||
              approveDisabled
            }
            startIcon={<CheckCircleRounded />}
            onClick={() => void submit("approve")}
          >
            {approveLabelOverride ?? "Approve"}
          </Button>
        )}
      </div>
    </div>
  );
};
